import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  Heading,
  Input,
  Skeleton,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Select,
  VStack,
} from "@chakra-ui/react";
import { format, subDays, startOfMonth, startOfQuarter, startOfYear } from "date-fns";
import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";

ChartJS.register(...registerables);

// ─── Channel label map ────────────────────────────────────────────────────────

const CHANNEL_LABELS = {
  chat: "Chat",
  web_call: "Web Call",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  fb_messenger: "Messenger",
  instagram: "Instagram",
  leadconnector: "LeadConnector",
  sms: "SMS",
  plivo_call: "Plivo",
  telnyx_call: "Telnyx",
  phone: "Phone",
  html_call: "HTML Call",
  html: "HTML",
  unknown: "Other",
};

function channelLabel(ch) {
  return CHANNEL_LABELS[ch] || ch;
}

// ─── No-data row helper ────────────────────────────────────────────────────────

function NoDataRow({ colSpan }) {
  return (
    <Tr>
      <Td colSpan={colSpan} textAlign="center" py={6} color="gray.400">
        No data
      </Td>
    </Tr>
  );
}

// ─── Rank badge ───────────────────────────────────────────────────────────────

function RankBadge({ rank }) {
  return (
    <Badge colorScheme="gray" fontSize="xs" borderRadius="full" px={2}>
      {rank}
    </Badge>
  );
}

// ─── Usage Trend Line Chart ───────────────────────────────────────────────────

function UsageTrendChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <Flex align="center" justify="center" h="260px">
        <Text fontSize="sm" color="gray.400">
          No usage data for this period
        </Text>
      </Flex>
    );
  }

  const labels = data.map((d) => d.date);

  const config = {
    data: {
      labels,
      datasets: [
        {
          label: "Voice Minutes",
          data: data.map((d) => d.voice_minutes),
          borderColor: "#7C3AED",
          backgroundColor: "rgba(124,58,237,0.08)",
          fill: false,
          tension: 0.4,
          pointRadius: labels.length > 30 ? 2 : 4,
          pointHoverRadius: 6,
          yAxisID: "yLeft",
        },
        {
          label: "Chat Messages",
          data: data.map((d) => d.chat_messages),
          borderColor: "#4F46E5",
          backgroundColor: "rgba(79,70,229,0.08)",
          fill: false,
          tension: 0.4,
          pointRadius: labels.length > 30 ? 2 : 4,
          pointHoverRadius: 6,
          yAxisID: "yLeft",
        },
        {
          label: "Credits Used",
          data: data.map((d) => d.credits_used),
          borderColor: "#F97316",
          backgroundColor: "rgba(249,115,22,0.08)",
          fill: false,
          tension: 0.4,
          pointRadius: labels.length > 30 ? 2 : 4,
          pointHoverRadius: 6,
          yAxisID: "yRight",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          display: true,
          position: "top",
          labels: { color: "#6B7280", font: { size: 12 } },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: "#9CA3AF", maxTicksLimit: 12 },
        },
        yLeft: {
          type: "linear",
          position: "left",
          beginAtZero: true,
          grid: { color: "rgba(156,163,175,0.15)" },
          ticks: { color: "#9CA3AF", precision: 0 },
          title: {
            display: true,
            text: "Voice Min / Chat Msgs",
            color: "#9CA3AF",
            font: { size: 11 },
          },
        },
        yRight: {
          type: "linear",
          position: "right",
          beginAtZero: true,
          grid: { drawOnChartArea: false },
          ticks: { color: "#F97316", precision: 0 },
          title: {
            display: true,
            text: "Credits Used",
            color: "#F97316",
            font: { size: 11 },
          },
        },
      },
    },
  };

  return (
    <div style={{ height: "260px", position: "relative" }}>
      <Line {...config} />
    </div>
  );
}

// ─── New Users Trend Bar Chart ────────────────────────────────────────────────

function UserActivityTrendChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <Flex align="center" justify="center" h="200px">
        <Text fontSize="sm" color="gray.400">No data for this period</Text>
      </Flex>
    );
  }
  const config = {
    data: {
      labels: data.map((d) => d.date),
      datasets: [
        { label: "New Signups", data: data.map((d) => d.signups), borderColor: "#10B981", backgroundColor: "rgba(16,185,129,0.15)", fill: true, tension: 0.3, pointRadius: 2 },
        { label: "Logins", data: data.map((d) => d.logins), borderColor: "#3B82F6", backgroundColor: "rgba(59,130,246,0.1)", fill: true, tension: 0.3, pointRadius: 2 },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 }, color: "#6B7280" } } },
      scales: {
        x: { grid: { display: false }, ticks: { color: "#9CA3AF", maxTicksLimit: 12 } },
        y: { beginAtZero: true, grid: { color: "rgba(156,163,175,0.15)" }, ticks: { color: "#9CA3AF", precision: 0 } },
      },
    },
  };
  return <div style={{ height: "200px", position: "relative" }}><Line {...config} /></div>;
}

// ─── Date range presets ──────────────────────────────────────────────────────

const DATE_PRESETS = [
  { label: "Last 7 days", from: () => subDays(new Date(), 7) },
  { label: "Last 30 days", from: () => subDays(new Date(), 30) },
  { label: "MTD", from: () => startOfMonth(new Date()) },
  { label: "QTD", from: () => startOfQuarter(new Date()) },
  { label: "YTD", from: () => startOfYear(new Date()) },
  { label: "Last 1 Year", from: () => subDays(new Date(), 365) },
];

// ─── Card skeleton ────────────────────────────────────────────────────────────

function CardSkeleton({ rows = 6 }) {
  return (
    <VStack align="stretch" spacing={3} p={4}>
      <Skeleton h="20px" w="40%" />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} h="16px" />
      ))}
    </VStack>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InsightoAdminAnalytics() {
  const baseUrl = getHostDomain();

  const [dateFrom, setDateFrom] = useState(
    format(subDays(new Date(), 30), "yyyy-MM-dd")
  );
  const [dateTo, setDateTo] = useState(format(new Date(), "yyyy-MM-dd"));

  const [status, setStatus] = useState("idle");
  const [analytics, setAnalytics] = useState(null);

  async function fetchAnalytics() {
    setStatus("loading");
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const res = await fetchController(
        `${baseUrl}/api/v1/insighto-admin/analytics?${params}`,
        "GET"
      );
      setAnalytics(res?.data || null);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  // Fetch on mount only; subsequent fetches triggered by Refresh button
  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = status === "loading";

  const {
    most_active_users = [],
    most_active_assistants = [],
    usage_trend = [],
    most_used_tools = [],
    channel_breakdown = [],
    active_agency_subaccounts = [],
    user_activity_trend = [],
  } = analytics || {};

  // Compute channel totals for percentage
  const channelTotal = channel_breakdown.reduce((s, d) => s + d.count, 0);

  return (
    <VStack h="100%" spacing={6} align="stretch">
      {/* Header */}
      <Flex align="center" gap={4} wrap="wrap">
        <Heading size="md" flex={1}>
          Admin Analytics
        </Heading>
        <Flex align="center" gap={2} wrap="wrap">
          <Select
            placeholder="Preset"
            size="sm"
            maxW="150px"
            onChange={(e) => {
              const preset = DATE_PRESETS[e.target.value];
              if (preset) {
                setDateFrom(format(preset.from(), "yyyy-MM-dd"));
                setDateTo(format(new Date(), "yyyy-MM-dd"));
              }
            }}
          >
            {DATE_PRESETS.map((p, i) => (
              <option key={p.label} value={i}>{p.label}</option>
            ))}
          </Select>
          <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
            From
          </Text>
          <Input
            type="date"
            size="sm"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            maxW="160px"
          />
          <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
            To
          </Text>
          <Input
            type="date"
            size="sm"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            maxW="160px"
          />
          <Button
            size="sm"
            colorScheme="purple"
            onClick={fetchAnalytics}
            isLoading={loading}
            loadingText="Loading"
          >
            Refresh
          </Button>
          {loading && <Spinner size="sm" color="purple.500" />}
        </Flex>
      </Flex>

      {/* Error state */}
      {status === "error" && (
        <Box
          p={4}
          bg="red.50"
          borderRadius="md"
          borderWidth={1}
          borderColor="red.200"
        >
          <Text color="red.600" fontSize="sm">
            Failed to load analytics. Please try refreshing.
          </Text>
        </Box>
      )}

      {/* 2-column grid for top cards */}
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
        {/* Most Active Users */}
        <Card variant="elevated">
          <CardBody p={0}>
            {loading ? (
              <CardSkeleton rows={10} />
            ) : (
              <>
                <Heading size="sm" px={4} pt={4} pb={3} borderBottomWidth={1} borderColor="gray.100">
                  Most Active Users
                </Heading>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th w="40px">#</Th>
                        <Th>Email</Th>
                        <Th>Name</Th>
                        <Th isNumeric>Conversations</Th>
                        <Th isNumeric>Chats</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {most_active_users.length === 0 ? (
                        <NoDataRow colSpan={5} />
                      ) : (
                        most_active_users.slice(0, 10).map((u, i) => (
                          <Tr key={u.user_id}>
                            <Td>
                              <RankBadge rank={i + 1} />
                            </Td>
                            <Td fontSize="sm">{u.email}</Td>
                            <Td fontSize="sm">{u.name || "—"}</Td>
                            <Td isNumeric fontSize="sm" fontWeight="semibold">
                              {u.conversation_count.toLocaleString()}
                            </Td>
                            <Td isNumeric fontSize="sm" color="gray.500">
                              {(u.total_chats || 0).toLocaleString()}
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </>
            )}
          </CardBody>
        </Card>

        {/* Most Active Assistants */}
        <Card variant="elevated">
          <CardBody p={0}>
            {loading ? (
              <CardSkeleton rows={10} />
            ) : (
              <>
                <Heading size="sm" px={4} pt={4} pb={3} borderBottomWidth={1} borderColor="gray.100">
                  Most Active Assistants
                </Heading>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th w="40px">#</Th>
                        <Th>Assistant Name</Th>
                        <Th>LLM Model</Th>
                        <Th>Owner Email</Th>
                        <Th isNumeric>Conversations</Th>
                        <Th isNumeric>Chats</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {most_active_assistants.length === 0 ? (
                        <NoDataRow colSpan={6} />
                      ) : (
                        most_active_assistants.slice(0, 10).map((a, i) => (
                          <Tr key={a.assistant_id}>
                            <Td>
                              <RankBadge rank={i + 1} />
                            </Td>
                            <Td fontSize="sm">{a.assistant_name || "—"}</Td>
                            <Td fontSize="sm"><Badge colorScheme="purple" fontSize="xs">{a.llm_model || "—"}</Badge></Td>
                            <Td fontSize="sm">{a.owner_email}</Td>
                            <Td isNumeric fontSize="sm" fontWeight="semibold">
                              {a.conversation_count.toLocaleString()}
                            </Td>
                            <Td isNumeric fontSize="sm" color="gray.500">
                              {(a.total_chats || 0).toLocaleString()}
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </>
            )}
          </CardBody>
        </Card>

        {/* Most Used Tools */}
        <Card variant="elevated">
          <CardBody p={0}>
            {loading ? (
              <CardSkeleton rows={10} />
            ) : (
              <>
                <Heading size="sm" px={4} pt={4} pb={3} borderBottomWidth={1} borderColor="gray.100">
                  Most Used Tools
                </Heading>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th w="40px">#</Th>
                        <Th>Tool Name</Th>
                        <Th>Function</Th>
                        <Th isNumeric>Invocations</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {most_used_tools.length === 0 ? (
                        <NoDataRow colSpan={4} />
                      ) : (
                        most_used_tools.slice(0, 10).map((t, i) => (
                          <Tr key={`${t.tool_name}-${t.function_name}`}>
                            <Td>
                              <RankBadge rank={i + 1} />
                            </Td>
                            <Td fontSize="sm">{t.tool_name}</Td>
                            <Td fontSize="sm" color="gray.500">
                              {t.function_name}
                            </Td>
                            <Td isNumeric fontSize="sm" fontWeight="semibold">
                              {t.invocation_count.toLocaleString()}
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </>
            )}
          </CardBody>
        </Card>

        {/* Channel Breakdown */}
        <Card variant="elevated">
          <CardBody p={0}>
            {loading ? (
              <CardSkeleton rows={10} />
            ) : (
              <>
                <Heading size="sm" px={4} pt={4} pb={3} borderBottomWidth={1} borderColor="gray.100">
                  Channel Breakdown
                </Heading>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Channel</Th>
                        <Th isNumeric>Conversations</Th>
                        <Th isNumeric>% of Total</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {channel_breakdown.length === 0 ? (
                        <NoDataRow colSpan={3} />
                      ) : (
                        channel_breakdown.map((c) => {
                          const pct = channelTotal
                            ? ((c.count / channelTotal) * 100).toFixed(1)
                            : "0.0";
                          return (
                            <Tr key={c.channel}>
                              <Td fontSize="sm">{channelLabel(c.channel)}</Td>
                              <Td isNumeric fontSize="sm" fontWeight="semibold">
                                {c.count.toLocaleString()}
                              </Td>
                              <Td isNumeric fontSize="sm" color="gray.500">
                                {pct}%
                              </Td>
                            </Tr>
                          );
                        })
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Active Agency Subaccounts — full width */}
      <Card variant="elevated">
        <CardBody p={0}>
          {loading ? (
            <CardSkeleton rows={8} />
          ) : (
            <>
              <Heading size="sm" px={4} pt={4} pb={3} borderBottomWidth={1} borderColor="gray.100">
                Active Agency Subaccounts
              </Heading>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th w="40px">#</Th>
                      <Th>Agency Email</Th>
                      <Th isNumeric>Conversations</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {active_agency_subaccounts.length === 0 ? (
                      <NoDataRow colSpan={3} />
                    ) : (
                      active_agency_subaccounts.slice(0, 10).map((a, i) => (
                        <Tr key={a.agency_id}>
                          <Td>
                            <RankBadge rank={i + 1} />
                          </Td>
                          <Td fontSize="sm">{a.agency_email}</Td>
                          <Td isNumeric fontSize="sm" fontWeight="semibold">
                            {a.conversation_count.toLocaleString()}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </>
          )}
        </CardBody>
      </Card>

      {/* Usage Trend + New Users Trend — side by side */}
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
        <Card variant="elevated">
          <CardBody>
            {loading ? (
              <CardSkeleton rows={8} />
            ) : (
              <>
                <Heading size="sm" mb={4}>
                  Usage Trend
                </Heading>
                <UsageTrendChart data={usage_trend} />
              </>
            )}
          </CardBody>
        </Card>
        <Card variant="elevated">
          <CardBody>
            {loading ? (
              <CardSkeleton rows={8} />
            ) : (
              <>
                <Heading size="sm" mb={4}>
                  Signups & Logins (Direct)
                </Heading>
                <UserActivityTrendChart data={user_activity_trend} />
              </>
            )}
          </CardBody>
        </Card>
      </Grid>
    </VStack>
  );
}
